import {
  buildCrewMembers,
  buildSafetyApprovals,
  buildSafetyMeasureItems,
  effectiveVoltageClass,
  excavationInProtectedZoneForRequest,
  observerRequirementSatisfied,
  safetyApprovalForRequest,
  type SafetyApprovalDraft,
  type SafetyMeasureDrafts,
  type VoltageClassDecision,
} from '@railroute/contracts/core';
// Только типы: корневой ствол контрактов панель читает в одном месте —
// `api.ts` (перепись потребителей, `legacy-compatibility-consumer-audit`).
import type {
  CreateWorkOrderDto,
  ElectricalGroup,
  InstallationVoltageClass,
  SubdivisionMember,
} from '../api';
import { admissionLabel } from '../sections/personnel-rows';

/**
 * Черновик выписки в панели → тело `POST /work-orders`.
 *
 * ЗДЕСЬ НЕТ НИ ОДНОЙ ПРОВЕРКИ ПО СУЩЕСТВУ, и это главное свойство модуля.
 * Требуемая группа по электробезопасности, состав по матрице требований,
 * наблюдающий, напряжение, согласование по пункту 8, полномочия названных
 * лиц — считает и отвергает СЕРВЕР. Панель его отказ показывает словами, а
 * не предугадывает: вторая проверка в клиенте разошлась бы с серверной молча
 * и выписывала бы наряды, которые норма не разрешает (или отказывала в тех,
 * которые разрешает).
 *
 * Графы пункта 5, роли бригады и признак согласования собираются ОБЩИМИ
 * функциями контрактов — теми же, которыми их собирает экран телефона.
 * Наряд, выписанный за столом, обязан получиться тем же нарядом, что и
 * выписанный в поле.
 */
export type IssueDraft = {
  // Номера в черновике нет: его ставит сервер при выдаче по порядку журнала
  // организации (решение владельца 25.09.2026).
  workType: string;
  safetyMeasures: string;
  /** Подразделение выдающего. В форме не выбирается — берётся из его членства. */
  subdivisionId: string;
  workSiteId: string | null;
  workSiteDetail: string;
  requiredElectricalGroup: ElectricalGroup | null;
  /**
   * Напряжение электроустановки (issue #463, карточка B-075) — РЕШЕНИЕ
   * выдающего, а не показанное значение.
   *
   * `undefined` — поле не трогали, и в нём стоит умолчание вида работ;
   * значение или `null` — выдающий выбрал сам, включая «не указано». Смена
   * вида работы меняет умолчание, но принятое решение не отменяет: иначе
   * снятое напряжение молча вернулось бы обратно.
   */
  voltageClassDecision: VoltageClassDecision;
  workTypeCatalogId: string | null;
  /** Ответственный руководитель работ (ADR-003: слитое легаси-поле `supervisorId`). */
  supervisorId: string | null;
  /** Допускающий. Пусто — сервер подставит ответственного руководителя. */
  admittingUserId: string | null;
  producerId: string | null;
  memberIds: string[];
  observerId: string | null;
  measures: SafetyMeasureDrafts;
  /** Пункт 8 Правил № 344: `null` — выдающий галочку не трогал. */
  safetyApprovalDecision: boolean | null;
  /**
   * Реквизиты согласования по пункту 8 (issue #468, карточка B-034): с кем
   * согласовано, кто согласовал и когда.
   *
   * Строки живут в черновике независимо от галочки: сняв и вернув её, выдающий
   * не должен вводить их заново. В запрос они уходят как есть, а требуется ли
   * согласование этому наряду и полны ли реквизиты — решает СЕРВЕР.
   */
  safetyApprovals: SafetyApprovalDraft[];
  /**
   * Земляные работы в охранной зоне подземных коммуникаций — пункт 300 Правил
   * № 222 (issue #469). `null` — выдающий галочку не трогал, и действует
   * умолчание справочника видов работ.
   */
  excavationDecision: boolean | null;
};

/**
 * Заполнено ли то, без чего запрос заведомо не соберётся.
 *
 * Это НЕ норма, а вежливость экрана: перечисленное ниже — обязательные поля
 * схемы запроса, и отправлять форму без них значит гарантированно получить
 * отказ и заставить человека заполнять её заново. Всё, что решает норма,
 * решает сервер.
 *
 * `requiresObserver` приходит из строки матрицы, которую панель ТОЛЬКО
 * прочитала у сервера. Считать требование здесь заново она не может и не
 * пытается: при расхождении права последнее слово всё равно за сервером.
 *
 * `proposedVoltage` — умолчание вида работ, то самое, что стоит в поле, пока
 * выдающий его не трогал. Напряжение обязательно с issue #464 (карточка
 * B-062): наряд без него сервер не выписывает, поэтому кнопка закрыта, пока в
 * поле пусто. Это по-прежнему НЕ проверка по существу, а то же избавление от
 * заведомого отказа, что и остальные строки ниже.
 */
export function draftIsComplete(
  draft: IssueDraft,
  requiresObserver: boolean,
  proposedVoltage: InstallationVoltageClass | null,
): boolean {
  return (
    draft.subdivisionId.length > 0 &&
    draft.workType.trim().length > 0 &&
    draft.safetyMeasures.trim().length > 0 &&
    draft.supervisorId !== null &&
    draft.producerId !== null &&
    draft.memberIds.length >= 1 &&
    effectiveVoltageClass(draft.voltageClassDecision, proposedVoltage) !== null &&
    observerRequirementSatisfied(requiresObserver, draft.observerId)
  );
}

/**
 * Черновик → тело запроса.
 *
 * Незаполненное НЕ отправляется пустой строкой: опущенное поле сервер
 * трактует умолчанием (место работ не названо, напряжение берётся из строки
 * матрицы, согласование — из справочника видов работ), а пустая строка была
 * бы названным пустым значением. Разница видна в бланке.
 *
 * `issuerId` не отправляется: выдающий — это тот, кто послал запрос, и
 * сервер ставит его сам.
 *
 * `proposedVoltage` — умолчание вида работ, то самое, что стоит в поле, пока
 * выдающий его не трогал. Оно передаётся сюда, а не читается из черновика,
 * потому что это не его решение, а прочитанный факт строки матрицы.
 */
export function buildCreateWorkOrderDto(
  draft: IssueDraft,
  proposedVoltage: InstallationVoltageClass | null,
  groupFromWorkType = false,
): CreateWorkOrderDto {
  if (draft.supervisorId === null || draft.producerId === null) {
    // Сюда не приходят: кнопка выписки закрыта `draftIsComplete`. Отказ
    // вместо пустой строки — чтобы неполный черновик не превратился в наряд
    // с безымянным руководителем работ, если гейт когда-нибудь разойдётся.
    throw new Error('Ответственный руководитель и производитель работ обязательны');
  }
  const measureItems = buildSafetyMeasureItems(draft.measures);
  const approval = safetyApprovalForRequest(draft.safetyApprovalDecision);
  const approvals = buildSafetyApprovals(draft.safetyApprovals);
  const excavation = excavationInProtectedZoneForRequest(draft.excavationDecision);
  return {
    workType: draft.workType.trim(),
    safetyMeasures: draft.safetyMeasures.trim(),
    subdivisionId: draft.subdivisionId,
    ...(draft.workSiteId ? { workSiteId: draft.workSiteId } : {}),
    ...(draft.workSiteDetail.trim() ? { workSiteDetail: draft.workSiteDetail.trim() } : {}),
    // Группу задаёт вид работ — её записывает сервер из матрицы, и форма её
    // не отправляет: выбранное когда-то до выбора вида работ значение ушло
    // бы в запрос и получило бы отказ за расхождение с матрицей.
    ...(!groupFromWorkType && draft.requiredElectricalGroup
      ? { requiredElectricalGroup: draft.requiredElectricalGroup }
      : {}),
    // Напряжение (issue #463) отправляется ВСЕГДА, включая пустое — в отличие
    // от полей выше. Опущенное поле сервер заменяет умолчанием строки матрицы,
    // и наряд получил бы напряжение, которого в форме не стояло. Подписывает
    // выдающий то, что видел на экране (ADR-004 §13), поэтому уходит ровно
    // показанное: выбранное — значением, «не указано» — явным null.
    voltageClass: effectiveVoltageClass(draft.voltageClassDecision, proposedVoltage),
    ...(draft.workTypeCatalogId ? { workTypeCatalogId: draft.workTypeCatalogId } : {}),
    supervisorId: draft.supervisorId,
    ...(draft.admittingUserId ? { admittingUserId: draft.admittingUserId } : {}),
    producerId: draft.producerId,
    members: buildCrewMembers(draft.memberIds, draft.observerId),
    ...(measureItems.length > 0 ? { safetyMeasureItems: measureItems } : {}),
    ...(approval === undefined ? {} : { requiresSafetyApproval: approval }),
    // Реквизиты согласования (issue #468). Пустой перечень не отправляется:
    // «согласований нет» и «поля нет» для сервера одно и то же, а лишнее
    // пустое поле в теле запроса читалось бы как названное пустое значение.
    ...(approvals.length > 0 ? { safetyApprovals: approvals } : {}),
    // Признак охранной зоны (issue #469). Галочку не трогали — поле опускается,
    // и умолчание берёт сервер из справочника: панель не пересказывает ему то,
    // что он и так знает.
    ...(excavation === undefined ? {} : { excavationInProtectedZone: excavation }),
  };
}

/**
 * Итог по работнику — тот же светофор и те же слова, что в разделе
 * «Персонал». `null` — сервер этого работника не считал, и говорить о нём
 * нечего.
 */
export function crewAdmissionLabel(member: SubdivisionMember): string | null {
  return member.overallStatus === undefined ? null : admissionLabel({ overallStatus: member.overallStatus });
}

// Отбор людей в списки ответственных — общий с формой выписки в телефоне,
// поэтому живёт в контрактах. Правил в нём нет: он читает ответ сервера.
export { assignablePeople, keepAssignable, type AssignableRole } from '@railroute/contracts/core';
